import { isDevEnv } from "../../../utils/analyticsEnv";
import { FALLBACK_AVATAR_OPTIONS, getAvatarThumbSrc } from "../../../utils/profileAvatarCatalog";
import { presenterLiveMatchCard } from "../../Leagues/lib/leaguePresenterLiveMatch";
import { BULLION_BONUS_POINTS_BY_TIER } from "./constants";
import { buildLuckyDipSelections } from "./bullionLuckyDip";
import { bullionBoardLineOddsForDisplay, bullionLinePotentialFromOdds } from "./bullionLineScoring";
import { bullionSelectionStorageKey } from "./bullionSelectionKeys";
import { buildCricketSyntheticPrematchRawMarkets } from "./bullionSyntheticPrematchMarkets";
import { buildBullionRooms } from "./roomUtils";
import type {
    BullionBonusPicks,
    BullionLeaderboardRow,
    BullionPlayState,
    BullionPublicGame,
    BullionRoom,
    BullionSelection,
    BullionSelectionMap,
} from "./types";

/**
 * Dev-only end-to-end BULLION demo (`/bullion?demo=match`): a fake match + arenas, the real pick board,
 * then a scripted settlement / leaderboard animation built from the user's actual picks. Never shown on
 * production (gated by {@link isDevEnv}). Sibling of the played-view-only `?demo=lb` sandbox.
 */
export const BULLION_DEMO_MATCH_ENABLED = isDevEnv();

export const BULLION_DEMO_MATCH_ID = "bullion-demo-match";
export const BULLION_DEMO_MATCH_NAME = "India v West Indies";
export const BULLION_DEMO_COMPETITION = "Cricket8 Exhibition Series";
/** Lobby demo card → demo flow entry (keep aligned with `isBullionDemoMatchFlowUrl`). */
export const BULLION_DEMO_MATCH_FLOW_PATH = "/bullion?demo=match";
export const BULLION_DEMO_VIEWER_USER_ID = 9101;
export const BULLION_DEMO_VIEWER_NAME = "You";

const AVATAR = FALLBACK_AVATAR_OPTIONS.map((item) => getAvatarThumbSrc(item) || item.url || null);

export function bullionDemoViewerAvatar(): string {
    return AVATAR[6] || "";
}

/** Full board fill for league reopen when the viewer is entered but picks were never persisted. */
export function buildBullionDemoFilledBoard(room: BullionRoom): {
    selections: BullionSelectionMap;
    bonusPicks: BullionBonusPicks;
} | null {
    const dip = buildLuckyDipSelections(room);
    if (!dip || Object.keys(dip.picks).length === 0) return null;
    return { selections: dip.picks, bonusPicks: dip.bonusPicks };
}

/** Demo public arenas shown on the demo match landing (entry tiers + believable player counts). */
export const BULLION_DEMO_ARENAS: ReadonlyArray<Pick<BullionPublicGame, "gameId" | "matchId" | "name" | "entryStake" | "prizePot" | "visibility" | "status" | "playersCount">> = [
    { gameId: 990101, matchId: BULLION_DEMO_MATCH_ID, name: "Public 100", entryStake: 100, prizePot: 100 * 6, visibility: "public", status: "open", playersCount: 6 },
    { gameId: 990102, matchId: BULLION_DEMO_MATCH_ID, name: "Public 500", entryStake: 500, prizePot: 500 * 12, visibility: "public", status: "open", playersCount: 12 },
    { gameId: 990103, matchId: BULLION_DEMO_MATCH_ID, name: "Public 1000", entryStake: 1000, prizePot: 1000 * 9, visibility: "public", status: "open", playersCount: 9 },
];

/** Demo kickoff a little in the future so the board renders in its prematch state. */
function demoStartIso(): string {
    return new Date(Date.now() + 60 * 60 * 1000).toISOString();
}

/**
 * Build the demo room from the shared synthetic prematch markets so the real pick board renders a full
 * TEAM / PLAYER / MATCH / INNINGS catalog. Runner odds carry the scoring used by the simulation.
 */
export function buildBullionDemoRoom(startsAtIso?: string | null): BullionRoom {
    const start = String(startsAtIso || "").trim() || demoStartIso();
    const rawMarkets = buildCricketSyntheticPrematchRawMarkets({
        id: BULLION_DEMO_MATCH_ID,
        name: BULLION_DEMO_MATCH_NAME,
    });
    const seed = {
        id: BULLION_DEMO_MATCH_ID,
        name: BULLION_DEMO_MATCH_NAME,
        sport: "cricket",
        competition: BULLION_DEMO_COMPETITION,
        start,
        markets: rawMarkets,
    };
    const room = buildBullionRooms([seed as never], 1)[0];
    if (room) return { ...room, startsAt: start };
    // Defensive fallback — should not happen since the synthetic markets are always non-empty.
    return {
        id: `bullion-${BULLION_DEMO_MATCH_ID}`,
        name: "BULLION demo",
        matchName: BULLION_DEMO_MATCH_NAME,
        matchId: BULLION_DEMO_MATCH_ID,
        sport: "CRICKET",
        competition: BULLION_DEMO_COMPETITION,
        startsAt: start,
        isLive: false,
        fixturePhase: "scheduled",
        skipBullionEntryCoinCheck: true,
        groups: [[], [], [], []],
        players: 6,
    };
}

/**
 * Live variant of the demo room for the settlement simulation. The played view only shows real points
 * (not a hard 0) once the leaderboard is out of its pre-match state, so the sim room must read as in-play:
 * a past start, an in-progress status string, and `isLive`/`fixturePhase: "live"`. Shares the base room's
 * `groups` so the user's picks (keyed by market storage key) still resolve.
 */
export function buildBullionDemoLiveRoom(baseRoom?: BullionRoom): BullionRoom {
    const base = baseRoom ?? buildBullionDemoRoom();
    const live = presenterLiveMatchCard();
    return {
        ...base,
        startsAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        status: live.statusLine,
        isLive: true,
        fixturePhase: "live",
    };
}

export type BullionDemoSimStep = {
    /** Human-readable label for the step (debug / aria). */
    label: string;
    selections: BullionSelectionMap;
    bonusPicks: BullionBonusPicks;
    leaderboard: BullionLeaderboardRow[];
    playState: BullionPlayState;
    /** Markets that just settled as wins this step — drives the played-view win celebration. */
    celebrateMarketNames: string[];
};

type ResolvedDemoLine = {
    pickKey: string;
    marketId: string;
    runnerId: string;
    marketName: string;
    selectionName: string;
    groupIndex: number;
    chips: number;
    odds: number;
    bonusPoints: number;
    potPoints: number;
    win: boolean;
};

const DEMO_OPPONENTS: ReadonlyArray<{ userId: number; name: string; avatarIndex: number; finalFactor: number }> = [
    { userId: 9102, name: "Giraffe Pro", avatarIndex: 0, finalFactor: 0.92 },
    { userId: 9103, name: "Croc King", avatarIndex: 1, finalFactor: 0.78 },
    { userId: 9104, name: "Rhino Ace", avatarIndex: 2, finalFactor: 0.62 },
    { userId: 9105, name: "Tiger Stripes", avatarIndex: 3, finalFactor: 0.48 },
    { userId: 9106, name: "Monkey Business", avatarIndex: 4, finalFactor: 0.33 },
    { userId: 9107, name: "Panda Smash", avatarIndex: 5, finalFactor: 0.27 },
    { userId: 9108, name: "Falcon Eye", avatarIndex: 7, finalFactor: 0.22 },
    { userId: 9109, name: "Lion Heart", avatarIndex: 8, finalFactor: 0.17 },
    { userId: 9110, name: "Wolf Pack", avatarIndex: 9, finalFactor: 0.12 },
];

/** Resolve the viewer's board picks into scored lines (odds + pot points from the real board scoring). */
function resolveDemoLines(
    room: BullionRoom,
    selections: BullionSelectionMap,
    bonusPicks: BullionBonusPicks,
): ResolvedDemoLine[] {
    const flat = room.groups.flatMap((g) => g);
    const lines: ResolvedDemoLine[] = [];
    for (const market of flat) {
        const pickKey = bullionSelectionStorageKey(market);
        const sel = selections[pickKey] ?? selections[market.id];
        if (!sel || sel.chips <= 0) continue;
        const runner = market.runners.find((r) => r.id === sel.runnerId);
        if (!runner) continue;
        const odds = bullionBoardLineOddsForDisplay(null, market, runner, sel);
        const bonus = bonusPicks.find(
            (b) => (b.marketId === pickKey || b.marketId === market.id) && b.runnerId === sel.runnerId,
        );
        const bonusPoints = bonus ? BULLION_BONUS_POINTS_BY_TIER[bonus.tier] ?? bonus.points : 0;
        const potPoints = bullionLinePotentialFromOdds(odds, sel.chips, bonusPoints);
        lines.push({
            pickKey,
            marketId: pickKey,
            runnerId: sel.runnerId,
            marketName: market.name,
            selectionName: runner.name,
            groupIndex: market.groupIndex,
            chips: sel.chips,
            odds,
            bonusPoints,
            potPoints,
            win: false,
        });
    }
    return lines;
}

function rankRows(rows: BullionLeaderboardRow[]): BullionLeaderboardRow[] {
    const sorted = [...rows].sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        // When tied (esp. 0 at demo start), keep the viewer last — lowest userId would wrongly put them 1st.
        const aIsViewer = a.userId === BULLION_DEMO_VIEWER_USER_ID;
        const bIsViewer = b.userId === BULLION_DEMO_VIEWER_USER_ID;
        if (aIsViewer && !bIsViewer) return 1;
        if (bIsViewer && !aIsViewer) return -1;
        return a.userId - b.userId;
    });
    return sorted.map((row, index) => ({ ...row, rank: index + 1 }));
}

/**
 * Build the scripted settlement simulation from the user's actual picks. Lowest-value lines settle first
 * (the ~bottom third as losses, the rest as wins) so the viewer climbs the leaderboard and takes the lead
 * by the final step. Opponents accrue points on a steady ramp toward a fraction of the viewer's total.
 */
export function buildBullionDemoSimulationSteps(
    room: BullionRoom,
    selections: BullionSelectionMap,
    bonusPicks: BullionBonusPicks,
    entryStake: number,
    options?: { batchSize?: number },
): BullionDemoSimStep[] {
    const lines = resolveDemoLines(room, selections, bonusPicks);
    // Settle small → big for a climactic finish; the lowest third lose, the rest win.
    const ordered = [...lines].sort((a, b) => a.potPoints - b.potPoints);
    const lossCount = Math.floor(ordered.length / 3);
    ordered.forEach((line, index) => {
        line.win = index >= lossCount;
    });

    const totalMarkets = ordered.length;
    const totalPotential = ordered.reduce((sum, l) => sum + l.potPoints, 0);
    const viewerFinalPoints = ordered.reduce((sum, l) => sum + (l.win ? l.potPoints : 0), 0);
    const playerCount = DEMO_OPPONENTS.length + 1;
    const prizePot = entryStake * playerCount;

    // Reveal one market per step (two only when the card is large) so each settlement is easy to follow.
    // League play-page sim can pass a larger batch so the walkthrough does not crawl.
    const batchSize = Math.max(
        1,
        Math.round(Number(options?.batchSize) || (totalMarkets > 14 ? 2 : 1)),
    );
    const settlementStepCount = Math.max(1, Math.ceil(totalMarkets / batchSize));
    const totalSteps = settlementStepCount + 1;

    const viewerAvatar = bullionDemoViewerAvatar();

    const steps: BullionDemoSimStep[] = [];
    for (let stepIndex = 0; stepIndex < totalSteps; stepIndex += 1) {
        const settledThrough = Math.min(totalMarkets, stepIndex * batchSize);
        const prevSettledThrough = Math.min(totalMarkets, Math.max(0, stepIndex - 1) * batchSize);
        const settledLines = ordered.slice(0, settledThrough);
        const newlySettled = ordered.slice(prevSettledThrough, settledThrough);

        const viewerPoints = settledLines.reduce((sum, l) => sum + (l.win ? l.potPoints : 0), 0);
        const viewerCorrect = settledLines.filter((l) => l.win).length;
        const correctBonuses = settledLines.filter((l) => l.win && l.bonusPoints > 0).length;

        // Per-step selections map with settlement fields on the lines settled so far.
        const stepSelections: BullionSelectionMap = {};
        for (const line of ordered) {
            const settled = settledLines.includes(line);
            const selection: BullionSelection = {
                marketId: line.pickKey,
                decimalMarketId: line.pickKey,
                marketName: line.marketName,
                selectionName: line.selectionName,
                runnerId: line.runnerId,
                chips: line.chips,
                odds: line.odds,
                potPoints: line.potPoints,
                linePotPoints: line.potPoints,
                groupIndex: line.groupIndex,
                settled,
                settlementOutcome: settled ? (line.win ? "win" : "lose") : undefined,
                settlementResult: settled ? line.selectionName : undefined,
                pointsFromLine: settled ? (line.win ? line.potPoints : 0) : null,
            };
            stepSelections[line.pickKey] = selection;
        }

        // Everyone starts at 0; opponents ramp up only after the viewer's first settlement so step 1
        // is a solo climb (no mass opponent jump + viewer drop from a false tied-at-zero lead).
        const opponentProgress =
            stepIndex <= 1 ? 0 : (stepIndex - 1) / Math.max(1, settlementStepCount - 1);
        const rows: BullionLeaderboardRow[] = DEMO_OPPONENTS.map((opp) => {
            const oppFinal = Math.round(viewerFinalPoints * opp.finalFactor) || Math.round(40 * opp.finalFactor);
            const points = Math.round(oppFinal * opponentProgress);
            const oppSettled = Math.round(totalMarkets * opponentProgress);
            return {
                rank: 0,
                userId: opp.userId,
                playerName: opp.name,
                avatarUrl: AVATAR[opp.avatarIndex] ?? null,
                points,
                potentialPoints: Math.max(oppFinal, Math.round(totalPotential * opp.finalFactor)),
                settledMarkets: oppSettled,
                totalMarkets,
                correctMarkets: Math.round(oppSettled * 0.66),
                correctBonuses: opponentProgress >= 0.5 ? 1 : 0,
                winnings: null,
            };
        });
        rows.push({
            rank: 0,
            userId: BULLION_DEMO_VIEWER_USER_ID,
            playerName: BULLION_DEMO_VIEWER_NAME,
            avatarUrl: viewerAvatar,
            points: viewerPoints,
            potentialPoints: totalPotential,
            settledMarkets: settledThrough,
            totalMarkets,
            correctMarkets: viewerCorrect,
            correctBonuses,
            winnings: null,
        });
        const leaderboard = rankRows(rows);
        const self = leaderboard.find((r) => r.userId === BULLION_DEMO_VIEWER_USER_ID)!;

        const playState: BullionPlayState = {
            id: 1,
            userId: BULLION_DEMO_VIEWER_USER_ID,
            gameId: 990001,
            matchId: room.matchId,
            matchName: room.matchName,
            competition: room.competition,
            startsAt: room.startsAt,
            entryStake,
            gameDisplayName: `Public ${entryStake}`,
            gameStatus: stepIndex >= totalSteps - 1 && settledThrough >= totalMarkets ? "SETTLED" : "IN_PLAY",
            oddsSnapshot: null,
            selections: { picks: stepSelections, bonusPicks },
            settled: settledThrough >= totalMarkets,
            settledMarkets: settledThrough,
            totalMarkets,
            potentialPoints: totalPotential,
            prizePot,
            playerCount,
            points: self.points,
            rank: self.rank,
            winnings: null,
        };

        steps.push({
            label:
                stepIndex === 0
                    ? "Match live — no settlements yet"
                    : `Settlement ${stepIndex} of ${settlementStepCount}`,
            selections: stepSelections,
            bonusPicks,
            leaderboard,
            playState,
            celebrateMarketNames: newlySettled.filter((l) => l.win).map((l) => l.marketName),
        });
    }

    return steps;
}

/** Entered card before Start the match — open picks, 0 points, no settlement juice. */
export function buildBullionDemoPrematchPlayedStep(
    room: BullionRoom,
    selections: BullionSelectionMap,
    bonusPicks: BullionBonusPicks,
    entryStake: number,
): BullionDemoSimStep | null {
    const steps = buildBullionDemoSimulationSteps(room, selections, bonusPicks, entryStake);
    const first = steps[0];
    if (!first) return null;
    return {
        ...first,
        label: "Prematch — card entered",
        celebrateMarketNames: [],
        playState: {
            ...first.playState,
            gameStatus: "OPEN",
            startsAt: room.startsAt,
        },
    };
}

function leagueBullionSimCompleteKey(leagueId: string): string {
    return `bullion:league-sim-complete:${String(leagueId || "").trim()}`;
}

export function readLeagueBullionDemoSimComplete(leagueId: string): boolean {
    if (typeof window === "undefined") return false;
    try {
        return window.sessionStorage.getItem(leagueBullionSimCompleteKey(leagueId)) === "1";
    } catch {
        return false;
    }
}

export const LEAGUE_BULLION_SIM_COMPLETE_EVENT = "c8:bullion-league-sim-complete";

export function markLeagueBullionDemoSimComplete(leagueId: string): void {
    if (typeof window === "undefined") return;
    const id = String(leagueId || "").trim();
    if (!id) return;
    try {
        window.sessionStorage.setItem(leagueBullionSimCompleteKey(id), "1");
        window.dispatchEvent(new CustomEvent(LEAGUE_BULLION_SIM_COMPLETE_EVENT, { detail: { leagueId: id } }));
    } catch {
        /* ignore */
    }
}

export type BullionDemoLobbyLineSettlement = {
    outcome: "won" | "lost";
    points: number;
    settlementResult: string | null;
    marketName: string;
};

function settlementFromDemoSelection(sel: BullionSelection): BullionDemoLobbyLineSettlement | null {
    if (!sel.settled) return null;
    const outcomeRaw = String(sel.settlementOutcome || "").toLowerCase();
    const won = outcomeRaw === "win" || outcomeRaw === "won";
    const awarded = Math.max(0, Math.round(Number(sel.pointsFromLine) || 0));
    const pot = Math.max(0, Math.round(Number(sel.potPoints ?? sel.linePotPoints) || 0));
    return {
        outcome: won ? "won" : "lost",
        points: won ? awarded || pot : 0,
        settlementResult: String(sel.settlementResult || sel.selectionName || "").trim() || null,
        marketName: String(sel.marketName || "").trim(),
    };
}

/**
 * League lobby overlay for the presenter demo: a handful of settled picks after Start the match,
 * then the full scripted slip once the play-page sim has been watched.
 */
export function bullionDemoLobbyLineSettlements(
    selections: BullionSelectionMap,
    bonusPicks: BullionBonusPicks,
    entryStake: number,
    simComplete: boolean,
): Map<string, BullionDemoLobbyLineSettlement> {
    if (!selections || Object.keys(selections).length === 0) return new Map();
    const steps = buildBullionDemoSimulationSteps(
        buildBullionDemoLiveRoom(),
        selections,
        bonusPicks,
        Math.max(0, Math.round(Number(entryStake) || 0)),
    );
    const last = steps[steps.length - 1];
    if (!last) return new Map();

    const collect = (keys: Set<string> | null) => {
        const map = new Map<string, BullionDemoLobbyLineSettlement>();
        for (const [key, sel] of Object.entries(last.selections)) {
            if (keys && !keys.has(key)) continue;
            const row = settlementFromDemoSelection(sel);
            if (row) map.set(key, row);
        }
        return map;
    };

    if (simComplete) return collect(null);

    const bonusKeys = new Set(
        (bonusPicks || []).map((bonus) => `${String(bonus.marketId)}:${String(bonus.runnerId)}`),
    );
    const lost: string[] = [];
    const won: string[] = [];
    for (const [key, sel] of Object.entries(last.selections)) {
        const row = settlementFromDemoSelection(sel);
        if (!row) continue;
        if (
            bonusKeys.has(`${key}:${String(sel.runnerId)}`) ||
            bonusKeys.has(`${sel.marketId}:${String(sel.runnerId)}`)
        ) {
            continue;
        }
        if (row.outcome === "lost") lost.push(key);
        else won.push(key);
    }
    return collect(new Set([...lost.slice(0, 2), ...won.slice(0, 2)]));
}
